import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { media, CANONICAL_ORIGIN, executive } from "@/data/site";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function MediaItem() {
  const { slug } = useParams();
  const item = media.find((m) => m.slug === slug);
  if (!item) return <Navigate to="/leadership/media" replace />;

  const url = `${CANONICAL_ORIGIN}/leadership/media/${item.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type":
      item.type === "Article" || item.type === "News"
        ? "NewsArticle"
        : item.type === "Interview"
        ? "Article"
        : item.type === "Podcast"
        ? "PodcastEpisode"
        : item.type === "Press Release"
        ? "NewsArticle"
        : "Event",
    name: item.title,
    headline: item.title,
    description: item.excerpt,
    datePublished: item.date,
    image: [item.image],
    author: { "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi` },
    publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    isPartOf: `${CANONICAL_ORIGIN}/leadership/media`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <>
      <Seo
        title={`${item.title} — ${item.type} | Adeshi Moffi`}
        description={item.excerpt}
        path={`/leadership/media/${item.slug}`}
        image={item.image}
        jsonLd={jsonLd}
      />

      <section className="pt-28 md:pt-36 pb-8">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Leadership", to: "/leadership" },
              { label: "Media", to: "/leadership/media" },
              { label: item.type },
            ]}
          />
          <Reveal>
            <div className="mt-10 section-num">{item.type}</div>
            <h1
              data-testid="media-item-title"
              className="h-display text-[2.6rem] md:text-[4.2rem] text-[#0A1128] mt-6 max-w-5xl leading-[0.98]"
            >
              {item.title}
            </h1>
            <div className="mt-8 border-t border-b border-[#E2E8F0] py-4 flex flex-wrap items-center gap-x-10 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#475569] font-sans">
              <span>Outlet: <span className="text-[#0A1128]">{item.outlet}</span></span>
              <span>{fmt(item.date)}</span>
              <span>{item.location}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <img src={item.image} alt={item.title} loading="lazy" className="w-full h-[380px] md:h-[520px] object-cover grayscale" />
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-3">
            <div className="section-num">Featured</div>
            <div className="mt-4 flex items-center gap-4">
              <img src={executive.headshot} alt={executive.headshotAlt} className="w-16 h-16 object-cover duotone-portrait" />
              <div>
                <div className="h-editorial text-lg text-[#0A1128]">{executive.name}</div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#475569] font-sans mt-1">{executive.title}</div>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <Reveal>
              <div className="prose-editorial max-w-3xl">
                <p>{item.excerpt}</p>
                <p>
                  For interviews, panel invitations, or press inquiries, please contact
                  the Office of the President at Investors Pathway LLC.
                </p>
              </div>
            </Reveal>
            <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
              <Link to="/leadership/media" data-testid="media-item-back" className="btn-outline">
                <ArrowLeft size={14} strokeWidth={1.5} /> All Media
              </Link>
              <Link to="/leadership/contact" data-testid="media-item-contact" className="btn-ink">
                Media Inquiries
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
